import { useRef, useState, type CSSProperties } from 'react';
import { Icon } from '@/components/Icon';
import { SearchBox } from '@/components/form/SearchBox';
import { Spinner } from '@/components/Spinner';
import { SortableHeader } from '@/components/SortableHeader';
import { useTooltip } from '@/hooks/useTooltip';
import { useElementWidth } from '@/hooks/useElementWidth';
import type { SortDir } from '@/lib/tableSort';
import type { ClientsPage as ClientsPageData } from '@/api/clients';
import { ClientRow } from './ClientRow';

/**
 * Колонки, по которым реально можно сортировать на бэке (Account::$allowedSorts,
 * см. докблок CustomerController::index() в Laravel) – "Подключено тестов",
 * "Кураторы", "Последняя активность" и "Дата подключения"
 * считаются на лету (withCount/подзапросы/relation) и сортировки не
 * поддерживают, поэтому у них обычный `<th>` без сортировки.
 */
export type ClientSortKey = 'name' | 'fio_admin' | 'status' | 'tariff_type' | 'balance' | 'validity';

/**
 * Чипы фильтра – реальный статус кабинета (Account.status), не выдуманный
 * лицензионный статус из макета (там `active/expired/expiring/no-license`,
 * которого в реальных данных просто нет).
 */
const STATUS_FILTERS: { value: string | null; label: string }[] = [
  { value: null, label: 'Все' },
  { value: 'active', label: 'Активные' },
  { value: 'demo', label: 'Демо' },
  { value: 'disable', label: 'Отключённые' },
];

export interface ClientsTableProps {
  data: ClientsPageData;
  isRefreshing: boolean;
  page: number;
  onPageChange: (page: number) => void;
  searchInput: string;
  onSearchChange: (value: string) => void;
  status: string | null;
  onStatusChange: (value: string | null) => void;
  sort: { key: ClientSortKey | null; dir: SortDir; toggle: (key: ClientSortKey) => void };
  onClientDeleted: () => void;
}

export function ClientsTable({
  data,
  isRefreshing,
  page,
  onPageChange,
  searchInput,
  onSearchChange,
  status,
  onStatusChange,
  sort,
  onClientDeleted,
}: ClientsTableProps) {
  const { items, meta } = data;
  const from = items.length === 0 ? 0 : (meta.currentPage - 1) * meta.perPage + 1;
  const to = (meta.currentPage - 1) * meta.perPage + items.length;
  const studioTooltip = useTooltip<HTMLTableCellElement>('Вход в Studio');
  const mt1Tooltip = useTooltip<HTMLTableCellElement>('Вход в Maintest 5i');
  // Левая позиция 2-й закреплённой колонки должна ТОЧНО совпадать с реальной
  // отрендеренной шириной 1-й — не отдельным подобранным числом в CSS
  // (те два значения легко рассинхронизировать при правке одной стороны).
  // Измеряем фактическую ширину и передаём её как inline left — single
  // source of truth вместо дублирования магического пикселя в CSS.
  const col1Ref = useRef<HTMLTableCellElement>(null);
  const col1Width = useElementWidth(col1Ref);
  const col2Style: CSSProperties | undefined = col1Width !== null ? { left: col1Width } : undefined;
  // Пока идёт удаление строки — морозим всю таблицу (не только меню строки),
  // тем же приёмом, что и "Создать" в мастере (см. wizard__submitting-overlay
  // в ClientCreatePage.tsx) — реальный DELETE тоже может сходить во внешние
  // системы и занять время, поведение должно быть явным, а не просто
  // задизейбленной кнопкой в одной строке.
  const [deletingId, setDeletingId] = useState<number | null>(null);

  return (
    <section className="clients">
      <header className="clients__header">
        <h1>Клиенты</h1>
        <p className="clients__lede">Показаны клиенты, закреплённые за вами · всего {meta.total}</p>
      </header>

      <div className="card card--flush">
        {deletingId !== null && (
          <div className="table-freeze-overlay" aria-live="polite">
            <Spinner label="Удаление…" />
          </div>
        )}

        <div className="table-toolbar">
          <SearchBox value={searchInput} onChange={onSearchChange} placeholder="Поиск по компании…" iconSize={16} />
          <div className="filter-chips">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.label}
                type="button"
                className={`filter-chip ${status === f.value ? 'filter-chip--active' : ''}`}
                onClick={() => onStatusChange(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className={`table-wrap ${isRefreshing ? 'table-wrap--refreshing' : ''}`}>
          <table className="table table--clients">
            <thead>
              <tr>
                <SortableHeader
                  ref={col1Ref}
                  label="Компания"
                  sortKey="name"
                  sort={sort}
                  onSort={sort.toggle}
                  className="sticky-col sticky-col--1"
                />
                <SortableHeader
                  label="Контактное лицо"
                  sortKey="fio_admin"
                  sort={sort}
                  onSort={sort.toggle}
                  className="sticky-col sticky-col--2"
                  style={col2Style}
                />
                <SortableHeader label="Статус" sortKey="status" sort={sort} onSort={sort.toggle} />
                <th ref={studioTooltip.ref} className="cell-center" {...studioTooltip.handlers}>
                  Studio
                  {studioTooltip.node}
                </th>
                <th ref={mt1Tooltip.ref} className="cell-center" {...mt1Tooltip.handlers}>
                  Maintest 5i
                  {mt1Tooltip.node}
                </th>
                <SortableHeader
                  label="Тип тарификации"
                  sortKey="tariff_type"
                  sort={sort}
                  onSort={sort.toggle}
                />
                <SortableHeader label="Баланс" sortKey="balance" sort={sort} onSort={sort.toggle} />
                <th>Подключено тестов</th>
                <th>Кураторы</th>
                <th>Последняя активность</th>
                <th>Дата подключения</th>
                <SortableHeader
                  label="Дата отключения"
                  sortKey="validity"
                  sort={sort}
                  onSort={sort.toggle}
                />
                <th aria-label="Действия" />
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={13} className="table__empty">
                    Нет клиентов, соответствующих фильтру.
                  </td>
                </tr>
              )}
              {items.map((client) => (
                <ClientRow
                  key={client.id}
                  client={client}
                  onDeleted={onClientDeleted}
                  col2Style={col2Style}
                  isDeleting={deletingId === client.id}
                  onDeletingChange={setDeletingId}
                />
              ))}
            </tbody>
          </table>
        </div>

        <div className="table-footer">
          <span className="table-footer__status">
            {isRefreshing && <span className="spinner__ring spinner__ring--sm" aria-hidden="true" />}
            {items.length === 0 ? 'Нет клиентов' : `Показано ${from}–${to} из ${meta.total}`}
          </span>
          <div className="pager">
            <button
              type="button"
              disabled={meta.currentPage <= 1}
              onClick={() => onPageChange(page - 1)}
              aria-label="Предыдущая страница"
            >
              <Icon name="chevronLeft" size={14} />
            </button>
            <button
              type="button"
              disabled={meta.currentPage >= meta.lastPage}
              onClick={() => onPageChange(page + 1)}
              aria-label="Следующая страница"
            >
              <Icon name="chevronRight" size={14} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
